/**
 * Global lighting injection. Owner: lighting.
 *
 * Everything here is gated behind `#ifdef AAA_LIGHTING`, and the define is only
 * ever set on materials this module explicitly registers, so any material we do
 * not touch compiles exactly as stock three.js does. The uniform *objects* are
 * shared by reference across every registered material, so updating them once
 * per frame updates the whole scene.
 *
 * What gets injected:
 *   - `aaaSunShadow()`  4-cascade PSSM with a PCSS blocker search, so shadows
 *                        are razor sharp at contact and widen with distance.
 *   - `aaaIrradianceVolume()`  SH-ish (hemisphere) bounce probes on a 3D grid,
 *                        plus a sky-visibility term that attenuates the IBL so
 *                        interiors stop being lit by the outdoor environment.
 *   - aerial perspective fog with height falloff, sky-cube in-scatter and
 *     sun-tinted forward scattering.
 */

import * as THREE from 'three';

export const CASCADE_COUNT = 4;

/* ------------------------------------------------------------------ *
 * Shared uniform objects — one instance, referenced by every material.
 * ------------------------------------------------------------------ */
export const sharedUniforms = {
  aaaSunDirection: { value: new THREE.Vector3(0, 1, 0) },

  aaaShadowAtlas: { value: null },
  aaaCascadeMatrix: { value: Array.from({ length: CASCADE_COUNT }, () => new THREE.Matrix4()) },
  // x: split far (view depth), y: texel world size, z: depth range (m), w: atlas tile index
  aaaCascadeInfo: { value: Array.from({ length: CASCADE_COUNT }, () => new THREE.Vector4(1, 0.01, 100, 0)) },
  // x: tiles per row, y: tile resolution, z: tan(sun angular radius), w: max penumbra (m)
  aaaShadowParams: { value: new THREE.Vector4(2, 1024, 0.021, 1.6) },
  // x: const depth bias (m), y: slope bias (m), z: normal offset (texels), w: strength
  aaaShadowBias: { value: new THREE.Vector4(0.02, 0.14, 1.6, 1.0) },
  aaaShadowFar: { value: 220 },

  aaaIvSky: { value: null },
  aaaIvGround: { value: null },
  aaaIvMin: { value: new THREE.Vector3(-1, -1, -1) },
  aaaIvInvSize: { value: new THREE.Vector3(1, 1, 1) },
  aaaIvTexelInset: { value: new THREE.Vector3(0.01, 0.01, 0.01) },
  // Half a probe cell in world units — the lookup is pushed this far along the
  // surface normal so a floor does not read the cell buried under it and a
  // ceiling does not read the cell above the roof. Set by Env.js at bake time.
  aaaIvNormalBias: { value: new THREE.Vector3(1.2, 1.0, 1.2) },
  aaaIvIntensity: { value: 0 },
  aaaSkyOcclusion: { value: 1 },   // strength of the specular sky-occlusion term
  /**
   * How much outdoor specular IBL survives at zero sky visibility.
   *
   * This is the single most sensitive number in the ambient balance and it is a
   * uniform so it can be ablated. It was 0.06, which — together with the volume
   * being sampled without a normal bias — deleted 92 % of the only ambient most
   * interior surfaces had. At 0.22 the interiors came back but went flat and
   * overlit. 0.10 is where a room reads as a room.
   */
  aaaSkyOccFloor: { value: 0.10 },
  // How much of the PMREM's *diffuse* irradiance survives. Once the irradiance
  // volume is baked it already carries the sky (including what reaches an
  // interior through a doorway) plus one bounce, so keeping the unoccluded
  // environment on top would double count and light every room from outside.
  aaaIblDiffuse: { value: 1 },

  aaaSkyCube: { value: null },
  // x: base density, y: 1/scaleHeight, z: uniform density, w: sky cube lod
  aaaFogParams: { value: new THREE.Vector4(0.0, 0.02, 0.0, 4.0) },
  aaaFogHeight: { value: 0 },
  aaaFogSunColor: { value: new THREE.Color(0, 0, 0) },
  aaaFogSkyTint: { value: new THREE.Color(1, 1, 1) },
  aaaFogSunG: { value: 0.72 },
  aaaFogMaxOpacity: { value: 1.0 },
  /**
   * Per-channel extinction, normalised to unit luminance so it changes the hue
   * of the haze without changing how much of it there is.
   *
   * Aerial perspective is not grey. Boundary-layer aerosol follows an Angstrom
   * law, sigma ~ lambda^-alpha with alpha ~ 0.8 for dust, so blue is removed
   * ~18 % faster than green and red ~13 % slower — and it is replaced by
   * in-scattered sky. A scalar transmittance can only wash a distant surface
   * toward the haze *value*; this is what also moves its *hue*, which is the
   * half of aerial perspective the review was calling monochrome.
   */
  aaaFogExtinction: { value: new THREE.Vector3(0.874, 1.0, 1.176) },
  /**
   * How much of the haze the camera is standing in — sky visibility at the
   * camera, from the baked irradiance volume, floored. Without it a shuttered
   * room accumulates outdoor haze over its own 8 m of depth and the interiors
   * go milky again, which is the shape of FINDINGS F1.
   */
  aaaFogIndoor: { value: 1.0 },
};

/* ------------------------------------------------------------------ *
 * GLSL
 * ------------------------------------------------------------------ */

const DECLS = /* glsl */ `
#ifdef AAA_LIGHTING

float aaaIGN( vec2 p ) {
  return fract( 52.9829189 * fract( dot( p, vec2( 0.06711056, 0.00583715 ) ) ) );
}

vec2 aaaVogel( int i, int count, float phi ) {
  float r = sqrt( ( float( i ) + 0.5 ) / float( count ) );
  float theta = float( i ) * 2.399963229728653 + phi;
  return vec2( cos( theta ), sin( theta ) ) * r;
}

uniform vec3 aaaSunDirection;

uniform sampler2D aaaShadowAtlas;
uniform mat4 aaaCascadeMatrix[ AAA_CASCADES ];
uniform vec4 aaaCascadeInfo[ AAA_CASCADES ];
uniform vec4 aaaShadowParams;
uniform vec4 aaaShadowBias;
uniform float aaaShadowFar;

uniform sampler3D aaaIvSky;
uniform sampler3D aaaIvGround;
uniform vec3 aaaIvMin;
uniform vec3 aaaIvInvSize;
uniform vec3 aaaIvTexelInset;
uniform vec3 aaaIvNormalBias;
uniform float aaaIvIntensity;
uniform float aaaSkyOcclusion;
uniform float aaaSkyOccFloor;
uniform float aaaIblDiffuse;

const vec4 AAA_UNPACK = vec4( ( 255.0 / 256.0 ) / vec3( 1.0, 256.0, 65536.0 ), 1.0 / 16777216.0 );

float aaaUnpackDepth( vec4 v ) { return dot( v, AAA_UNPACK ); }

float aaaSampleAtlas( vec2 uv, float tile ) {
  float tpr = aaaShadowParams.x;
  vec2 tileOrigin = vec2( mod( tile, tpr ), floor( tile / tpr ) );
  vec2 auv = ( tileOrigin + clamp( uv, vec2( 0.0015 ), vec2( 0.9985 ) ) ) / tpr;
  return aaaUnpackDepth( texture2D( aaaShadowAtlas, auv ) );
}

/**
 * Contact-hardening sun shadow. Cascade chosen by view depth (dithered at the
 * split so the transition is noise instead of a visible line), normal-offset +
 * slope-scaled bias, PCSS blocker search then a variable-radius Vogel PCF.
 */
float aaaSunShadow( vec3 worldPos, vec3 worldNormal, float viewDepth ) {
  if ( aaaShadowBias.w <= 0.0 ) return 1.0;

  float ign = aaaIGN( gl_FragCoord.xy );
  float selectDepth = viewDepth * ( 1.0 + ( ign - 0.5 ) * 0.09 );

  int c = AAA_CASCADES - 1;
  for ( int i = 0; i < AAA_CASCADES; i ++ ) {
    if ( selectDepth < aaaCascadeInfo[ i ].x ) { c = i; break; }
  }

  vec4 info = aaaCascadeInfo[ c ];
  float texel = info.y;
  float range = info.z;
  float tile = info.w;
  float orthoSize = texel * aaaShadowParams.y;

  float NdotL = dot( worldNormal, aaaSunDirection );
  if ( NdotL <= 0.0 ) return 0.0;                 // self-shadowed by geometry
  float slope = sqrt( max( 0.0, 1.0 - NdotL * NdotL ) ) / max( NdotL, 0.12 );
  slope = min( slope, 6.0 );

  vec3 offsetPos = worldPos + worldNormal * ( texel * aaaShadowBias.z * ( 1.0 + slope * 1.2 ) );

  vec4 sc = aaaCascadeMatrix[ c ] * vec4( offsetPos, 1.0 );
  vec3 uvz = sc.xyz / sc.w;
  if ( uvz.x < 0.0 || uvz.x > 1.0 || uvz.y < 0.0 || uvz.y > 1.0 || uvz.z > 1.0 || uvz.z < 0.0 ) return 1.0;

  float receiver = uvz.z;
  float bias01 = ( aaaShadowBias.x + aaaShadowBias.y * slope ) / range;

  float phi = ign * 6.283185307;

  // ---- blocker search
  float searchUV = min( aaaShadowParams.w, orthoSize * 0.08 ) / orthoSize;
  float blockerSum = 0.0;
  float blockerCount = 0.0;
  for ( int i = 0; i < 8; i ++ ) {
    float d = aaaSampleAtlas( uvz.xy + aaaVogel( i, 8, phi ) * searchUV, tile );
    if ( d < receiver - bias01 ) { blockerSum += d; blockerCount += 1.0; }
  }
  if ( blockerCount < 0.5 ) return 1.0;

  float avgBlocker = blockerSum / blockerCount;
  float blockerDist = max( 0.0, ( receiver - avgBlocker ) * range );
  float penumbra = clamp( blockerDist * aaaShadowParams.z, texel * 0.9, aaaShadowParams.w );
  float filterUV = max( penumbra / orthoSize, 0.8 / aaaShadowParams.y );

  // ---- variable-radius PCF
  float sum = 0.0;
  for ( int i = 0; i < 16; i ++ ) {
    float d = aaaSampleAtlas( uvz.xy + aaaVogel( i, 16, phi + 1.7 ) * filterUV, tile );
    sum += step( receiver - bias01, d );
  }
  float shadow = sum * 0.0625;

  // Fade the last cascade out rather than popping to unshadowed.
  shadow = mix( shadow, 1.0, smoothstep( 0.82, 1.0, viewDepth / aaaShadowFar ) );

  return mix( 1.0, shadow, aaaShadowBias.w );
}

vec4 aaaIvFetch( vec3 worldPos, out float fade ) {
  vec3 uvw = ( worldPos - aaaIvMin ) * aaaIvInvSize;
  vec3 cl = clamp( uvw, aaaIvTexelInset, vec3( 1.0 ) - aaaIvTexelInset );
  fade = 1.0 - smoothstep( 0.0, 0.12, length( uvw - clamp( uvw, vec3( 0.0 ), vec3( 1.0 ) ) ) );
  return vec4( cl, 1.0 );
}

/** Local one-bounce irradiance from the baked probe grid. */
vec3 aaaIrradianceVolume( vec3 worldPos, vec3 n ) {
  if ( aaaIvIntensity <= 0.0 ) return vec3( 0.0 );
  float fade;
  vec3 cl = aaaIvFetch( worldPos + n * aaaIvNormalBias, fade ).xyz;
  vec3 sky = texture( aaaIvSky, cl ).rgb;
  vec3 gnd = texture( aaaIvGround, cl ).rgb;
  float w = clamp( n.y * 0.5 + 0.5, 0.0, 1.0 );
  return mix( gnd, sky, w ) * ( aaaIvIntensity * fade );
}

/**
 * How much open sky this point can see. Used to occlude the *specular* IBL —
 * a reflection probe cannot tell it is indoors, this can. Floored so interiors
 * still show some sheen instead of going matte-black.
 */
float aaaSkyVisibility( vec3 worldPos, vec3 n ) {
  #ifdef AAA_LOCAL_ENV
  /**
   * This material's envMap is a local reflection probe baked inside the room —
   * it already IS the correct local environment, walls, practicals and the
   * daylight coming through the opening. Occluding it by sky visibility on top
   * of that deleted 85-92 % of the only ambient those surfaces had, which is
   * how every probe-lit interior surface in the build ended up 5-10x too dark
   * while the walls next to them (on the sky environment, unprobed) read fine.
   */
  return 1.0;
  #else
  if ( aaaIvIntensity <= 0.0 ) return 1.0;
  float fade;
  vec3 cl = aaaIvFetch( worldPos + n * aaaIvNormalBias, fade ).xyz;
  float v = texture( aaaIvSky, cl ).a;
  // The lookup is now normal-biased into the room, so v at a ceiling is the
  // ceiling's own visibility instead of a blend with the open sky above the
  // roof. That is what stops cloud shapes appearing on interior ceilings.
  v = mix( 1.0, aaaSkyOccFloor + ( 1.0 - aaaSkyOccFloor ) * v, aaaSkyOcclusion * fade );
  return clamp( v, 0.0, 1.0 );
  #endif
}
#endif
`;

const FOG_DECLS = /* glsl */ `
#ifdef AAA_LIGHTING
uniform samplerCube aaaSkyCube;
uniform vec4 aaaFogParams;
uniform float aaaFogHeight;
uniform vec3 aaaFogSunColor;
uniform vec3 aaaFogSkyTint;
uniform float aaaFogSunG;
uniform float aaaFogMaxOpacity;
uniform vec3 aaaFogExtinction;
uniform float aaaFogIndoor;

float aaaFogPhase( float mu, float g ) {
  float g2 = g * g;
  float d = 1.0 + g2 - 2.0 * g * mu;
  return ( 1.0 - g2 ) / ( 4.0 * 3.141592653589793 * max( 1e-3, d * sqrt( max( 1e-3, d ) ) ) );
}
#endif
`;

const FOG_BODY = /* glsl */ `
#if defined( USE_FOG ) && defined( AAA_LIGHTING )

	{
		vec3 camToFrag = vAaaWorldPos - cameraPosition;
		float fogDist = length( camToFrag );
		vec3 fogDir = camToFrag / max( fogDist, 1e-4 );

		float k = aaaFogParams.y;
		float h0 = ( cameraPosition.y - aaaFogHeight ) * k;
		float h1 = ( vAaaWorldPos.y - aaaFogHeight ) * k;
		float dh = h1 - h0;

		float od;
		if ( abs( dh ) < 1e-4 ) {
			od = aaaFogParams.x * exp( -h0 ) * fogDist;
		} else {
			od = aaaFogParams.x * fogDist * ( exp( -h0 ) - exp( -h1 ) ) / dh;
		}
		od = ( max( od, 0.0 ) + aaaFogParams.z * fogDist ) * aaaFogIndoor;

		// Per-channel: blue is scattered out of the path faster than red, and
		// what replaces it is the sky. That is what makes distance go cool and
		// low-contrast rather than merely pale.
		vec3 trans = exp( -od * aaaFogExtinction );
		trans = mix( vec3( 1.0 ), trans, aaaFogMaxOpacity );

		/**
		 * What lights the haze, when you are looking DOWN.
		 *
		 * Sampling the sky cube along the view direction is right for a
		 * horizontal or upward sightline — the haze in front of a distant
		 * rooftop really is backed by that piece of sky. It is wrong looking
		 * down: the cube below the horizon holds the ground term
		 * (uGroundAlbedo * sunlight), so an aerial shot hazed a distant block
		 * toward *more sand colour*. That is exactly the review's "monochrome
		 * salmon from the near rooftop at (400,950) to the far skyline at
		 * (1700,100)" — the haze was there, and it was the same hue as the
		 * thing it was hazing.
		 *
		 * The column of air between camera and target is lit from above
		 * whichever way the camera points, so below the horizon the source is
		 * blended toward the upper sky. Two mips coarser because it stands for
		 * a hemisphere, not a direction.
		 */
		vec3 skyCol = textureLod( aaaSkyCube, fogDir, aaaFogParams.w ).rgb;
		vec3 skyAbove = textureLod( aaaSkyCube, vec3( 0.0, 1.0, 0.0 ), aaaFogParams.w + 2.0 ).rgb;
		skyCol = mix( skyCol, skyAbove, clamp( -fogDir.y * 1.6, 0.0, 0.55 ) );
		float mu = dot( fogDir, aaaSunDirection );
		vec3 inscatter = skyCol * aaaFogSkyTint + aaaFogSunColor * ( aaaFogPhase( mu, aaaFogSunG ) * 4.0 );

		/**
		 * The haze along this sightline is only as bright as the sky it can see.
		 *
		 * Without this the in-scatter is the full unoccluded sky cube however
		 * deep in a canyon the path runs, and a 25 m sightline down level_alley
		 * gains ~7 % of a 3-unit horizon on surfaces sitting at 0.05 units — i.e.
		 * it more than doubles them. That is FINDINGS F1's veil rebuilt out of
		 * fog instead of out of exposure, and it would have taken the bounce
		 * result with it. The irradiance volume's visibility channel at the
		 * receiving surface is the cheapest honest proxy for how much of the
		 * path is open to the sky; the 0.22 floor keeps a shaded street reading
		 * as depth rather than snapping to zero haze.
		 */
		float ivFade;
		vec3 ivUvw = aaaIvFetch( vAaaWorldPos + vec3( 0.0, 1.0, 0.0 ) * aaaIvNormalBias, ivFade ).xyz;
		float openSky = aaaIvIntensity > 0.0
			? mix( 1.0, texture( aaaIvSky, ivUvw ).a, clamp( ivFade, 0.0, 1.0 ) )
			: 1.0;
		inscatter *= 0.22 + 0.78 * clamp( openSky, 0.0, 1.0 );

		gl_FragColor.rgb = gl_FragColor.rgb * trans + inscatter * ( 1.0 - trans );
	}

#elif defined( USE_FOG )

	#ifdef FOG_EXP2
		float fogFactor = 1.0 - exp( - fogDensity * fogDensity * vFogDepth * vFogDepth );
	#else
		float fogFactor = smoothstep( fogNear, fogFar, vFogDepth );
	#endif

	gl_FragColor.rgb = mix( gl_FragColor.rgb, fogColor, fogFactor );

#endif
`;

let installed = false;

/** Records which chunk patches actually matched — surfaced on ctx.lighting.diagnostics. */
export const patchReport = { ok: [], failed: [] };

function patch(chunk, find, replace) {
  const C = THREE.ShaderChunk;
  const before = C[chunk];
  const after = before.replace(find, replace);
  if (after === before) patchReport.failed.push(`${chunk}: ${String(find).slice(0, 48)}`);
  else patchReport.ok.push(chunk);
  C[chunk] = after;
}

/** Patch the global ShaderChunk table. Idempotent. */
export function installShaderPatches() {
  if (installed) return;
  installed = true;

  const C = THREE.ShaderChunk;

  // --- world position varying, reusing three's instancing/batching-correct path
  C.common = `#ifdef AAA_LIGHTING\nvarying vec3 vAaaWorldPos;\n#endif\n` + C.common;

  patch('worldpos_vertex',
    '#if defined( USE_ENVMAP ) || defined( DISTANCE ) || defined ( USE_SHADOWMAP ) || defined ( USE_TRANSMISSION ) || NUM_SPOT_LIGHT_COORDS > 0',
    '#if defined( AAA_LIGHTING ) || defined( USE_ENVMAP ) || defined( DISTANCE ) || defined ( USE_SHADOWMAP ) || defined ( USE_TRANSMISSION ) || NUM_SPOT_LIGHT_COORDS > 0');

  patch('worldpos_vertex',
    '\tworldPosition = modelMatrix * worldPosition;',
    '\tworldPosition = modelMatrix * worldPosition;\n\n\t#ifdef AAA_LIGHTING\n\tvAaaWorldPos = worldPosition.xyz;\n\t#endif');

  // --- declarations (fragment only — lights_pars_begin is never in a vertex shader)
  C.lights_pars_begin = DECLS + C.lights_pars_begin;

  // --- sun shadow term, matched by direction so light ordering cannot break it
  patch('lights_fragment_begin',
    'getDirectionalLightInfo( directionalLight, directLight );',
    `getDirectionalLightInfo( directionalLight, directLight );

		#ifdef AAA_LIGHTING
		{
			vec3 aaaSunView = normalize( ( viewMatrix * vec4( aaaSunDirection, 0.0 ) ).xyz );
			if ( dot( normalize( directionalLight.direction ), aaaSunView ) > 0.999 ) {
				// geometryNormal is in VIEW space; every one of our terms is
				// authored in world space, so rotate it back before use.
				directLight.color *= aaaSunShadow( vAaaWorldPos, transformNormalByInverseViewMatrix( geometryNormal, viewMatrix ), vViewPosition.z );
			}
		}
		#endif`,
  );

  // --- local bounce irradiance
  patch('lights_fragment_begin',
    'vec3 irradiance = getAmbientLightIrradiance( ambientLightColor );',
    `vec3 irradiance = getAmbientLightIrradiance( ambientLightColor );

	#ifdef AAA_LIGHTING
	irradiance += aaaIrradianceVolume( vAaaWorldPos, transformNormalByInverseViewMatrix( geometryNormal, viewMatrix ) );
	#endif`);

  // --- sky visibility attenuates the outdoor IBL so interiors read as interiors
  patch('lights_fragment_maps',
    '\t\t\tiblIrradiance += getIBLIrradiance( geometryNormal );',
    `\t\t\t#ifdef AAA_LIGHTING
			iblIrradiance += getIBLIrradiance( geometryNormal ) * aaaIblDiffuse;
			#else
			iblIrradiance += getIBLIrradiance( geometryNormal );
			#endif`);

  patch('lights_fragment_maps',
    '\t\tradiance += getIBLRadiance( geometryViewDir, geometryNormal, material.roughness );',
    `\t\t#ifdef AAA_LIGHTING
		radiance += getIBLRadiance( geometryViewDir, geometryNormal, material.roughness ) * mix( 1.0, aaaSkyVisibility( vAaaWorldPos, transformNormalByInverseViewMatrix( geometryNormal, viewMatrix ) ), 0.92 );
		#else
		radiance += getIBLRadiance( geometryViewDir, geometryNormal, material.roughness );
		#endif`);

  // --- aerial perspective
  C.fog_pars_fragment = FOG_DECLS + C.fog_pars_fragment;
  C.fog_fragment = FOG_BODY;
}

/* ------------------------------------------------------------------ *
 * Material registration
 * ------------------------------------------------------------------ */

const registered = new WeakSet();

function attach(material) {
  if (!material || registered.has(material)) return;
  if (!material.isMeshStandardMaterial && !material.isMeshPhysicalMaterial &&
      !material.isMeshLambertMaterial && !material.isMeshPhongMaterial) return;
  registered.add(material);

  material.defines = material.defines || {};
  material.defines.AAA_LIGHTING = 1;
  material.defines.AAA_CASCADES = CASCADE_COUNT;

  const prev = material.onBeforeCompile;
  material.onBeforeCompile = function (shader, renderer) {
    for (const k in sharedUniforms) shader.uniforms[k] = sharedUniforms[k];
    if (prev) prev.call(this, shader, renderer);
  };
  material.needsUpdate = true;
}

/** Subtrees other teams own that must keep stock three lighting. */
const SKIP_PREFIX = ['postfx.', 'probe_'];

function skipSubtree(obj) {
  const n = obj.name;
  if (!n) return false;
  for (const p of SKIP_PREFIX) if (n.startsWith(p)) return true;
  return false;
}

/**
 * Register every lit material reachable from `root`. Cheap enough to re-run
 * every few seconds so materials created after boot still get lit correctly.
 */
export function registerSceneMaterials(root) {
  const walk = (obj) => {
    if (skipSubtree(obj)) return;
    const m = obj.material;
    if (m) {
      if (Array.isArray(m)) m.forEach(attach);
      else attach(m);
    }
    const c = obj.children;
    for (let i = 0; i < c.length; i++) walk(c[i]);
  };
  walk(root);
}

export function registerMaterial(m) {
  attach(m);
}

/**
 * Re-attach after a material's onBeforeCompile was rebuilt from scratch
 * (surface detail-tier switch re-runs applySurfaceShading, which replaces the
 * hook this module wrapped — without this the shared lighting uniforms are
 * never bound again and the material renders black).
 */
export function reRegisterMaterial(m) {
  registered.delete(m);
  attach(m);
}
